/**
 * PAYMENT STATUS CONTEXT
 * 
 * This context manages payment status for users across both authentication methods:
 * 1. Auth0 authenticated users
 * 2. Custom JWT authenticated users (post-purchase)
 * 
 * PAYMENT STATUS LOGIC:
 * - Fetches payment status from backend API
 * - Works with unified authentication system
 * - Supports both Auth0 and custom JWT tokens
 * - Caches payment status to avoid repeated API calls
 * - Automatically refreshes when authentication state changes
 * 
 * API INTEGRATION:
 * - Uses unified token from UnifiedAuthContext
 * - Supports both Auth0 access tokens and custom JWT tokens
 * - Backend payment endpoint handles both token types
 * 
 * ERROR HANDLING:
 * - Graceful degradation when payment status unavailable
 * - Retry logic for failed payment status checks
 * - Comprehensive error logging
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import getApiBaseUrl from '../utils/apiBaseUrl';
import readJsonResponse from '../utils/readJsonResponse';

// Create the context with a default value
const PaymentStatusContext = createContext({
  isPayingCustomer: false,
  hasProOrAllAccess: false, // For bootcamp access - requires pro or all-access plan
  productSlugs: [], // Active product slugs for access checks
  hasResolvedPaymentStatus: false,
  isLoading: true,
  error: null,
  requiresSessionRefresh: false,
  refreshPaymentStatus: () => {}
});

export { PaymentStatusContext };

export function usePaymentStatus() {
  return useContext(PaymentStatusContext);
}

export function PaymentStatusProvider({ children }) {
  const [isPayingCustomer, setIsPayingCustomer] = useState(false);
  const [hasProOrAllAccess, setHasProOrAllAccess] = useState(false);
  const [productSlugs, setProductSlugs] = useState([]);
  const [hasResolvedPaymentStatus, setHasResolvedPaymentStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresSessionRefresh, setRequiresSessionRefresh] = useState(false);
  const [checkAttempts, setCheckAttempts] = useState(0);

  // Track if we need to force refresh on first fetch after auth
  // This is critical for post-purchase flows where the user just got a subscription
  const needsInitialForceRefresh = useRef(true);
  const lastAuthenticatedUserId = useRef(null);
  const inFlightPaymentStatusRequest = useRef(null);

  /**
   * UNIFIED AUTHENTICATION INTEGRATION
   * Gets authentication state from the unified auth system
   */
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    authMethod,
    getApiToken
  } = useUnifiedAuth();

  // Extract stable user identifiers to prevent re-render cascades
  // Using individual fields instead of entire user object maintains stable dependencies
  const userEmail = user?.email;
  const userSub = user?.sub || user?.auth0Sub || user?.id;

  /**
   * PAYMENT STATUS FETCHING FUNCTION
   * Retrieves current payment status from backend API using unified authentication
   * Uses stable user identifiers to prevent unnecessary re-fetches
   */
  const fetchPaymentStatus = useCallback((options = {}) => {
    const forceRefresh = Boolean(options.forceRefresh);
    // Don't fetch if not authenticated or still loading auth
    if (!isAuthenticated || authLoading) {
      console.debug('[PaymentStatusContext] Skipping payment status fetch - not authenticated or auth loading');
      setIsLoading(false);
      setIsPayingCustomer(false);
      setHasProOrAllAccess(false);
      setProductSlugs([]);
      setHasResolvedPaymentStatus(false);
      return Promise.resolve();
    }

    if (!userEmail) {
      console.debug('[PaymentStatusContext] Skipping payment status fetch - no user email');
      setIsLoading(false);
      setIsPayingCustomer(false);
      setHasProOrAllAccess(false);
      setProductSlugs([]);
      setHasResolvedPaymentStatus(false);
      return Promise.resolve();
    }

    const requestKey = userSub || userEmail;
    if (inFlightPaymentStatusRequest.current?.key === requestKey) {
      return inFlightPaymentStatusRequest.current.promise;
    }

    const request = (async () => {
      try {
        setIsLoading(true);
        setError(null);
        setRequiresSessionRefresh(false);

        console.debug('[PaymentStatusContext] Fetching payment status for:', userEmail, 'via', authMethod, 'forceRefresh:', forceRefresh);

        // Get appropriate token for API request
        const token = await getApiToken();
        if (!token) {
          console.warn('[PaymentStatusContext] No API token available');
          setIsLoading(false);
          setError('SESSION_TOKEN_UNAVAILABLE');
          setRequiresSessionRefresh(true);
          setCheckAttempts(prev => prev + 1);
          return;
        }

        const baseUrl = `${getApiBaseUrl()}/stripe/is-paying-customer`;
        const url = forceRefresh ? `${baseUrl}?forceRefresh=true` : baseUrl;
        const response = await fetchWithTimeout(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await readJsonResponse(response, { errorMessage: 'Payment status check failed' });
          console.debug('[PaymentStatusContext] Payment status response:', {
            isPaying: data.isPaying,
            hasProOrAllAccess: data.hasProOrAllAccess,
            productSlugs: data.productSlugs,
            authMethod,
            userEmail: userEmail
          });

          setIsPayingCustomer(data.isPaying || false);
          setHasProOrAllAccess(data.hasProOrAllAccess || false);
          setProductSlugs(data.productSlugs || []);
          setHasResolvedPaymentStatus(true);
          setRequiresSessionRefresh(false);
          setCheckAttempts(0); // Reset attempts on success
        } else {
          console.warn('[PaymentStatusContext] Payment status API error:', response.status);
          setError(`Payment status check failed: ${response.status}`);
          if (response.status === 401 || response.status === 403) {
            setHasResolvedPaymentStatus(false);
            setRequiresSessionRefresh(true);
            setCheckAttempts(prev => prev + 1);
          } else {
            // Server/network failures are not proof that a customer is unpaid.
            // Keep the last resolved entitlement state while retries run.
            setCheckAttempts(prev => prev + 1);
          }
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          console.warn('[PaymentStatusContext] Payment status fetch timed out');
          setError('Payment status check timed out');
        } else {
          console.error('[PaymentStatusContext] Payment status fetch error:', error);
          setError(error.message);
        }
        // A timeout or transient network failure is an unknown state, not an
        // unpaid state. Preserve any last known entitlements while retrying.
        setRequiresSessionRefresh(false);

        // Increment attempt counter for retry logic
        setCheckAttempts(prev => prev + 1);
      } finally {
        setIsLoading(false);
      }
    })();

    inFlightPaymentStatusRequest.current = { key: requestKey, promise: request };
    request.finally(() => {
      if (inFlightPaymentStatusRequest.current?.promise === request) {
        inFlightPaymentStatusRequest.current = null;
      }
    });
    return request;
  }, [isAuthenticated, authLoading, userEmail, userSub, authMethod, getApiToken]);

  /**
   * AUTHENTICATION STATE MONITORING
   * Automatically fetch payment status when authentication state changes
   *
   * CRITICAL: Uses forceRefresh for first fetch after authentication to bypass
   * backend cache. This is essential for post-purchase flows where user just
   * got a subscription but backend might have cached their pre-purchase status.
   * Uses stable user identifiers to prevent re-render cascades.
   */
  useEffect(() => {
    // Fetch payment status when authentication state stabilizes
    if (!authLoading && isAuthenticated && userSub) {
      // Check if this is a new user session or first fetch after auth
      const isNewSession = lastAuthenticatedUserId.current !== userSub;
      const shouldForceRefresh = isNewSession || needsInitialForceRefresh.current;

      if (isNewSession) {
        console.debug('[PaymentStatusContext] New user session detected, resetting force refresh flag');
        needsInitialForceRefresh.current = true;
        lastAuthenticatedUserId.current = userSub;
      }

      console.debug('[PaymentStatusContext] Authentication stable, fetching payment status', {
        shouldForceRefresh,
        isNewSession,
        authMethod,
        needsInitialForceRefresh: needsInitialForceRefresh.current
      });

      // Use forceRefresh on first fetch after auth to bypass backend cache
      // This is CRITICAL for post-purchase flows where user just subscribed
      fetchPaymentStatus({ forceRefresh: shouldForceRefresh });

      // Clear the initial force refresh flag after first fetch
      needsInitialForceRefresh.current = false;
    } else if (!authLoading && !isAuthenticated) {
      // User is definitely not authenticated, clear payment status
      console.debug('[PaymentStatusContext] User not authenticated, clearing payment status');
      setIsPayingCustomer(false);
      setHasProOrAllAccess(false);
      setProductSlugs([]);
      setHasResolvedPaymentStatus(false);
      setIsLoading(false);
      setError(null);
      setRequiresSessionRefresh(false);
      // Reset refs for next login
      needsInitialForceRefresh.current = true;
      lastAuthenticatedUserId.current = null;
    }
  }, [authLoading, isAuthenticated, userSub, authMethod, fetchPaymentStatus]);

  /**
   * RETRY LOGIC FOR FAILED REQUESTS
   * Automatically retry payment status checks with exponential backoff
   */
  useEffect(() => {
    if (checkAttempts > 0 && checkAttempts < 3) {
      const retryDelay = Math.pow(2, checkAttempts) * 1000; // Exponential backoff
      console.debug(`[PaymentStatusContext] Retrying payment status check in ${retryDelay}ms (attempt ${checkAttempts})`);
      
      const timer = setTimeout(() => {
        fetchPaymentStatus();
      }, retryDelay);
      
      return () => clearTimeout(timer);
    }
  }, [checkAttempts, fetchPaymentStatus]);

  /**
   * COMPREHENSIVE STATE LOGGING
   * Track payment status changes for debugging
   * Uses stable userEmail to prevent excessive logging
   */
  useEffect(() => {
    console.debug('[PaymentStatusContext] State summary:', {
      isAuthenticated,
      authMethod,
      userEmail,
      isPayingCustomer,
      hasProOrAllAccess,
      productSlugs,
      hasResolvedPaymentStatus,
      isLoading,
      error,
      requiresSessionRefresh,
      checkAttempts
    });
  }, [isAuthenticated, authMethod, userEmail, isPayingCustomer, hasProOrAllAccess, productSlugs, hasResolvedPaymentStatus, isLoading, error, requiresSessionRefresh, checkAttempts]);

  // Public refresh function
  const refreshPaymentStatus = useCallback((options = {}) => {
    console.debug('[PaymentStatusContext] Manual payment status refresh requested', options);
    setCheckAttempts(0); // Reset retry counter
    fetchPaymentStatus(options);
  }, [fetchPaymentStatus]);

  const contextValue = {
    isPayingCustomer,
    hasProOrAllAccess,  // For bootcamp access - requires pro or all-access plan
    productSlugs,       // Active product slugs for access checks
    hasResolvedPaymentStatus,
    isLoading,
    error,
    requiresSessionRefresh,
    refreshPaymentStatus
  };

  return (
    <PaymentStatusContext.Provider value={contextValue}>
      {children}
    </PaymentStatusContext.Provider>
  );
}
